'use client';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import Text from '@/components/common/Text/Text';

import UpsertAddressForm from '@/components/form/UpsertAddressForm/UpsertAddressForm';
import UpdatePaymentForm from '@/components/form/UpdatePaymentForm/UpdatePaymentForm';

import { useEllaAnalytics } from '@/components/hook/analytics';
import { usePayment } from '@/components/hook/payment';
import { useUser } from '@/components/hook/user';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import {
  getGenericCart,
  getTaxRateFromStripe,
  updateCartItemTax
} from '@/util/cart_generic_client';

import { EPCCAddress } from '@/types/User';
import { stripeOptions } from '@/const/stripe';

import './EllaCartShippingBilling.style.scss';
import {
  ELLA_RETAILER,
  EllaProductData
} from '../EllaCartProduct/EllaCartProduct';

import analytics from '@/util/analytics';
import { getMostRecentShippingAddress } from '@/util/user_client';
import { FixedShippingAddress } from '@/util/shipping_method_checkout';
import Stripe from 'stripe';

type EllaCartShippingBillingProps = {
  /** Member's addresses, including billing and shipping */
  addresses: EPCCAddress[];
  shippingMethod: string;
  /** Dispatch function to update parent state */
  dispatch?: (action: any) => void;
  /** Current bill state */
  bill: {
    subtotal: number;
    shipping: number;
    taxes: number;
    discountPercentage: number;
    taxRate: number;
  };

  cardDetails: {
    brand: string;
    last4Digits: string;
  } | null;

  /** Cart items to update tax for
   * Only considering revolve items
   */
  cartItems?: EllaProductData[];
  /** Cart ID */
  revolveCartId?: string;

  className?: string;
  canEdit?: boolean;

  vivrelleCart?: EllaProductData[];

  /**
   * Set when the selected Vivrelle shipping method always ships to a fixed
   * address (e.g. Thompson ship-to-hotel). REVOLVE / FWRD items still ship to
   * the member's own address.
   */
  fixedShippingAddress?: FixedShippingAddress;

  setNewlyAttachedPaymentMethod?: (paymentMethod: Stripe.PaymentMethod) => void;
};

export type UpdateState = {
  isUpdatingPayment: boolean;
  isUpdatingPaymentAddress: boolean;
  isPaymentUpdateSuccessful: boolean;
  isUpdatingRevolveShipping: boolean;
  isRevolveShippingUpdateSuccessful: boolean;
  isUpdatingAddress: boolean;
  isAddressUpdateSuccessful: boolean;
};

const resetFormStates = {
  isUpdatingRevolveShipping: false,
  isUpdatingAddress: false,
  isUpdatingPaymentAddress: false,
  isUpdatingPayment: false
};

const initialFormStates = {
  ...resetFormStates,
  isPaymentUpdateSuccessful: false,
  isRevolveShippingUpdateSuccessful: false,
  isAddressUpdateSuccessful: false
};

type AccountFormName = 'contact' | 'password' | 'address' | 'payment';

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''
);

function EllaCartShippingBilling(props: EllaCartShippingBillingProps) {
  const {
    className = '',
    addresses = [],
    shippingMethod = '',
    canEdit = true,
    dispatch,
    bill,
    cartItems = [],
    vivrelleCart = [],
    revolveCartId,
    setNewlyAttachedPaymentMethod,
    cardDetails,
    fixedShippingAddress
  } = props;

  const revolveCartItems = useMemo(() => {
    return cartItems.filter((item) => item.provider !== ELLA_RETAILER.VIVRELLE);
  }, [cartItems]);

  // Like NYC pickup, the Vivrelle order then has its own destination (the BFF
  // ships it to the fixed address), while REVOLVE / FWRD items still ship to
  // the member's address.
  const vivrelleShipsToFixedAddress =
    !!fixedShippingAddress && vivrelleCart.length > 0;

  const { value: user } = useUser();
  const { metadata } = useEllaAnalytics();

  const [userAddresses, setUserAddresses] = useState<EPCCAddress[]>(addresses);
  const [updateState, setUpdateState] =
    useState<UpdateState>(initialFormStates);

  // Sync local state with addresses prop changes
  useEffect(() => {
    setUserAddresses(addresses);
  }, [addresses]);

  const shippingAddress = useMemo(
    () => getMostRecentShippingAddress(userAddresses),
    [userAddresses]
  );

  const handleCancelEdit = () => {
    setUpdateState({
      ...updateState,
      ...resetFormStates
    });
  };

  const handleEdit = (formName: AccountFormName) => {
    let updateFields;

    switch (formName) {
      case 'password':
        updateFields = {
          isUpdatingRevolveShipping: true,
          isRevolveShippingUpdateSuccessful: false
        };
        break;

      case 'contact':
        updateFields = {
          isUpdatingContact: true,
          isContactUpdateSuccessful: false
        };
        break;

      case 'address':
        updateFields = {
          isUpdatingAddress: true,
          isAddressUpdateSuccessful: false
        };
        break;

      case 'payment':
        updateFields = {
          isUpdatingPayment: true,
          isPaymentUpdateSuccessful: false
        };
        break;
    }

    setUpdateState({
      ...updateState,
      ...resetFormStates,
      ...updateFields
    });
  };

  const handleUpdateSuccessful = async (formName: AccountFormName) => {
    let updateFields: Partial<UpdateState> = {};

    try {
      switch (formName) {
        case 'password':
          updateFields = {
            isUpdatingRevolveShipping: false,
            isRevolveShippingUpdateSuccessful: true
          };
          break;

        case 'contact':
          updateFields = {};
          break;

        case 'address':
          try {
            const response = await fetch('/api/user/addresses');
            const addressData = await response.json();
            if (addressData?.data?.addresses) {
              setUserAddresses(addressData.data.addresses);
              if (dispatch) {
                dispatch({
                  type: 'SET_ADDRESSES',
                  payload: {
                    addresses: addressData.data.addresses
                  }
                });
              }

              // Recalculate tax rate if shipping address was updated
              const shippingAddress = addressData.data.addresses.find(
                (address: EPCCAddress) => address.isBilling === false
              );

              if (shippingAddress) {
                try {
                  const taxRate =
                    (await getTaxRateFromStripe(shippingAddress)) ?? 0;

                  if (cartItems && revolveCartId) {
                    await Promise.all(
                      cartItems.map(async (item) => {
                        if (item.id && item.taxItemId) {
                          try {
                            await updateCartItemTax(
                              revolveCartId,
                              item.id,
                              taxRate,
                              item.taxItemId
                            );
                          } catch (error) {
                            console.error(
                              `Error updating tax for item ${item.id}:`,
                              error
                            );
                          }
                        }
                      })
                    );

                    const updatedCart = await getGenericCart(revolveCartId);

                    if (updatedCart && dispatch) {
                      dispatch({
                        type: 'SET_BILL',
                        payload: {
                          bill: {
                            subtotal: Number(
                              updatedCart.data.meta.display_price.without_tax
                                .amount
                            ),
                            taxes: Number(
                              updatedCart.data.meta.display_price.tax.amount
                            ),
                            taxRate: taxRate
                          }
                        }
                      });
                    }
                  }
                } catch (error) {
                  console.error('Error calculating tax rate:', error);
                }
              }
            }
          } catch (error) {
            console.error('Error fetching updated addresses:', error);
          }

          updateFields = {
            isUpdatingAddress: false,
            isAddressUpdateSuccessful: true
          };
          break;

        case 'payment':
          updateFields = {
            isUpdatingPayment: false,
            isUpdatingPaymentAddress: false,
            isPaymentUpdateSuccessful: true
          };
          break;
      }

      setUpdateState((prevState) => ({
        ...prevState,
        ...updateFields
      }));
    } catch (error) {
      console.error('Error in handleUpdateSuccessful:', error);
    }
  };

  const handlePaymentAddressToggle = () => {
    setUpdateState({
      ...updateState,
      isUpdatingPaymentAddress: !updateState.isUpdatingPaymentAddress
    });
  };

  return (
    <div className={cn('EllaCartShippingBilling', className)}>
      <div
        className={cn('EllaCartShippingBilling-sections', {
          /** These classes allow us to arrange grid layout depending on which forms are open */
          'edit-address': updateState.isUpdatingAddress,
          'edit-payment': updateState.isUpdatingPayment,
          'edit-payment-extended': updateState.isUpdatingPaymentAddress
        })}
      >
        {/* Vivrelle Pickup Items */}
        {shippingMethod.includes('NYC Pickup') && (
          <>
            <div
              className={cn(
                'AccountPayment-section',
                'AccountPayment-section-address'
              )}
            >
              <Text
                className='AccountPayment-title'
                variant='title-xxs-500-upper'
              >
                Vivrelle Pickup:
              </Text>
              <Text variant='text-s'>
                Vivrelle Showroom
                <br />
                44 E 32nd St.
                <br />
                Floor 6<br />
                New York, NY
                <br />
                10021
              </Text>
            </div>
          </>
        )}

        {/* Vivrelle fixed-address shipping (e.g. Thompson ship-to-hotel) */}
        {vivrelleShipsToFixedAddress && fixedShippingAddress && (
          <div
            className={cn(
              'AccountPayment-section',
              'AccountPayment-section-address'
            )}
          >
            <Text
              className='AccountPayment-title'
              variant='title-xxs-500-upper'
            >
              Vivrelle Shipping:
            </Text>
            <Text variant='text-s'>
              {fixedShippingAddress.line_1}
              {fixedShippingAddress.line_2 && (
                <>
                  <br />
                  {fixedShippingAddress.line_2}
                </>
              )}
              <br />
              {fixedShippingAddress.city}, {fixedShippingAddress.region}{' '}
              {fixedShippingAddress.postcode}
            </Text>
          </div>
        )}

        {/* Standard Shipping */}
        {(vivrelleCart.length > 0 || revolveCartItems.length > 0) &&
          !shippingMethod.includes('NYC Pickup') &&
          !vivrelleShipsToFixedAddress && (
            <div
              className={cn(
                'AccountPayment-section',
                'AccountPayment-section-address'
              )}
            >
              {shippingAddress && !updateState.isUpdatingAddress && (
                <>
                  <Text
                    className='AccountPayment-title title'
                    variant='title-xxs-500-upper'
                  >
                    Shipping:
                  </Text>
                  <Text variant='text-s'>
                    {shippingAddress.line_1}
                    {shippingAddress.line_2 && (
                      <>
                        <br />
                        {shippingAddress.line_2}
                      </>
                    )}
                    <br />
                    {shippingAddress.city}, {shippingAddress.region}{' '}
                    {shippingAddress.postcode}
                  </Text>

                  {updateState.isAddressUpdateSuccessful && (
                    <Text
                      variant='text-s'
                      className='AccountPayment-updated-success-text'
                    >
                      Address updated successfully!
                    </Text>
                  )}

                  {canEdit && (
                    <Text
                      className={cn(
                        'Link',
                        'link-s',
                        'compact',
                        'underline',
                        'AccountPayment-cta'
                      )}
                      variant='text-s'
                      onClick={() => {
                        handleEdit('address');
                      }}
                    >
                      Edit Shipping
                    </Text>
                  )}
                </>
              )}

              {shippingAddress && updateState.isUpdatingAddress && (
                <UpsertAddressForm
                  onCancel={handleCancelEdit}
                  onSubmitSuccess={() => {
                    analytics.track('Ella AI Checkout Address - Updated', {
                      ...metadata,
                      shippingAddress
                    });
                    handleUpdateSuccessful('address');
                  }}
                  isDefault={true}
                  initialValues={{
                    addressId: shippingAddress.id,
                    firstName: shippingAddress.first_name,
                    lastName: shippingAddress.last_name,
                    address: shippingAddress.line_1,
                    address2: shippingAddress.line_2 || '',
                    city: shippingAddress.city,
                    state: shippingAddress.region,
                    postalCode: shippingAddress.postcode,
                    phone: shippingAddress.phone_number
                  }}
                />
              )}
            </div>
          )}

        {/* Revolve & FWRD Shipping */}
        {cartItems.length > 0 &&
          vivrelleCart.length > 0 &&
          revolveCartItems.length > 0 &&
          (shippingMethod.includes('NYC Pickup') ||
            vivrelleShipsToFixedAddress) && (
            <div
              className={cn(
                'AccountPayment-section',
                'AccountPayment-section-address'
              )}
            >
              {shippingAddress && !updateState.isUpdatingAddress && (
                <>
                  <Text
                    className='AccountPayment-title title'
                    variant='title-xxs-500-upper'
                  >
                    REVOLVE & FWRD Shipping:
                  </Text>
                  <Text variant='text-s'>
                    {shippingAddress.line_1}
                    {shippingAddress.line_2 && (
                      <>
                        <br />
                        {shippingAddress.line_2}
                      </>
                    )}
                    <br />
                    {shippingAddress.city}, {shippingAddress.region}{' '}
                    {shippingAddress.postcode}
                  </Text>

                  {updateState.isAddressUpdateSuccessful && (
                    <Text
                      variant='text-s'
                      className='AccountPayment-updated-success-text'
                    >
                      Address updated successfully!
                    </Text>
                  )}

                  {canEdit && (
                    <Text
                      className={cn(
                        'Link',
                        'link-s',
                        'compact',
                        'underline',
                        'AccountPayment-cta'
                      )}
                      variant='text-s'
                      onClick={() => {
                        handleEdit('address');
                      }}
                    >
                      Edit Shipping
                    </Text>
                  )}
                </>
              )}

              {shippingAddress && updateState.isUpdatingAddress && (
                <UpsertAddressForm
                  onCancel={handleCancelEdit}
                  onSubmitSuccess={() => {
                    handleUpdateSuccessful('address');
                  }}
                  isDefault={true}
                  initialValues={{
                    addressId: shippingAddress.id,
                    firstName: shippingAddress.first_name,
                    lastName: shippingAddress.last_name,
                    address: shippingAddress.line_1,
                    address2: shippingAddress.line_2 || '',
                    city: shippingAddress.city,
                    state: shippingAddress.region,
                    postalCode: shippingAddress.postcode,
                    phone: shippingAddress.phone_number
                  }}
                />
              )}
            </div>
          )}

        {/* billing */}
        <div
          className={cn(
            'AccountPayment-section',
            'AccountPayment-section-payment'
          )}
        >
          {!updateState.isUpdatingPayment && (
            <Text
              className='AccountPayment-title title'
              variant='title-xxs-500-upper'
            >
              Billing:
            </Text>
          )}

          {cardDetails && updateState.isUpdatingPayment && (
            <Elements
              stripe={stripePromise}
              options={stripeOptions}
            >
              <UpdatePaymentForm
                initialValues={{
                  billingAddressSameAsShipping: true,
                  firstName: '',
                  lastName: '',
                  phone: '',
                  address: '',
                  address2: '',
                  city: '',
                  postalCode: '',
                  state: ''
                }}
                addresses={addresses}
                onAddressToggle={handlePaymentAddressToggle}
                onCancel={handleCancelEdit}
                onSubmitSuccess={() => {
                  analytics.track(
                    'Ella AI Checkout Billing Address - Updated',
                    {
                      ...metadata,
                      billingAddress: addresses
                    }
                  );
                  handleUpdateSuccessful('payment');
                }}
                setNewlyAttachedPaymentMethod={setNewlyAttachedPaymentMethod}
              />
            </Elements>
          )}

          {cardDetails && !updateState.isUpdatingPayment && (
            <>
              <Text variant='text-s'>
                {user?.name}
                <br />
                {cardDetails.brand} ****{cardDetails.last4Digits}
                <br />
              </Text>
              {updateState.isPaymentUpdateSuccessful && (
                <Text
                  variant='text-s'
                  className='AccountPayment-updated-success-text'
                >
                  Payment updated successfully!
                </Text>
              )}

              {canEdit && (
                <Text
                  className={cn(
                    'Link',
                    'link-s',
                    'compact',
                    'underline',
                    'AccountPayment-cta'
                  )}
                  variant='text-s'
                  onClick={() => {
                    handleEdit('payment');
                  }}
                >
                  Edit Payment
                </Text>
              )}
            </>
          )}

          {!cardDetails && <Text variant='text-s'>-</Text>}
        </div>
      </div>
    </div>
  );
}

export default EllaCartShippingBilling;
